import { type JSONContent } from "@tiptap/core";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";

import { findFieldNodeById, paragraphToReportText } from "../components/tiptap/extensions";
import type { FieldNodeAttrs } from "../components/tiptap/marks";
import type {
  CandidateAttribute,
  MergeField,
  Phrasing,
  ReportSection,
} from "../hooks/useReportQuery";
import type { ReportTemplate } from "../hooks/useTemplate";
import { GPT_IMPRESSION_DISCLOSURE } from "./impressionDisclosure";
import type { ImpressionFinding } from "./impressionStream";
import { logger } from "./logger";

export type PMInsertContent = Array<Record<string, unknown>>;

/** Read the exact current-exam fields that the Impression service may cite.
 * Both the live editor and unopened-report export call this function, keeping
 * source selection independent of the output surface. */
export function extractFindingsForImpression(doc: JSONContent): ImpressionFinding[] {
  const findingsSection = (doc.content ?? []).find((node) => node.type === "findingsSection");
  if (!findingsSection) return [];

  const findings: ImpressionFinding[] = [];
  for (const subsection of findingsSection.content ?? []) {
    if (subsection.type !== "organSubsection") continue;
    const section = typeof subsection.attrs?.kind === "string" ? subsection.attrs.kind : "";
    const visit = (node: JSONContent): void => {
      if (node.type === "field") {
        const attrs = node.attrs ?? {};
        const id = typeof attrs.id === "string" ? attrs.id : "";
        const text = typeof attrs.text === "string" ? attrs.text.trim() : "";
        if (!id || !text) return;
        findings.push({
          id,
          section,
          text,
          seriesId: typeof attrs.seriesId === "string" ? attrs.seriesId : null,
          sliceIndex: typeof attrs.sliceIndex === "number" ? attrs.sliceIndex : null,
        });
        return;
      }
      for (const child of node.content ?? []) visit(child);
    };
    visit(subsection);
  }
  return findings;
}

/** True only for the retired canned-Impression representation. This is a
 * migration marker, never a source of new report content. */
export function hasLegacyTemplateImpression(state: unknown): boolean {
  let root = state;
  if (typeof root === "string") {
    try {
      root = JSON.parse(root) as unknown;
    } catch {
      return false;
    }
  }
  const visit = (node: unknown): boolean => {
    if (!node || typeof node !== "object") return false;
    if (Array.isArray(node)) return node.some(visit);
    const value = node as { type?: unknown; attrs?: { provenance?: unknown }; content?: unknown };
    if (value.type === "impressionItem" && value.attrs?.provenance === "template") return true;
    return visit(value.content);
  };
  return visit(root);
}

/**
 * For unknown study coverage, the report response is the presentation
 * contract. Build the editor layout from those self-describing sections so a
 * future model compartment can render without a matching frontend release.
 */
export function templateForReportSections(
  template: ReportTemplate,
  sections: Record<string, ReportSection>,
  templateMismatch: boolean,
): ReportTemplate {
  if (!templateMismatch) return template;
  return {
    ...template,
    findings: Object.entries(sections).map(([kind, section]) => ({
      kind,
      label: section.label?.trim() || kind.replaceAll(/[-_]+/g, " ").toUpperCase(),
      default: section.defaultText ?? "",
    })),
  };
}

// Marks from earlier schemas that the new editor doesn't recognize. The
// `field` mark belonged to the previous schema (now an inline node); a draft
// saved while it was a mark loads with marks silently dropped, leaving plain
// text where chips should be. Loading marks named here is a stale draft.
const STALE_MARK_NAMES = new Set(["bracket", "insertedHighlight", "findingLink", "field"]);

export function hasStaleMarks(node: unknown): boolean {
  if (!node || typeof node !== "object") return false;
  if (Array.isArray(node)) return node.some(hasStaleMarks);
  const n = node as { marks?: Array<{ type?: string }>; content?: unknown };
  if (Array.isArray(n.marks) && n.marks.some((m) => m?.type && STALE_MARK_NAMES.has(m.type))) {
    return true;
  }
  return hasStaleMarks(n.content);
}

// A saved draft from before the typed-section cutover stores findings as
// `<p><strong>LIVER:</strong> ...</p>` paragraphs at the doc root. The new
// schema expects a top-level `findingsSection` node; without it nothing
// renders as a section. Discard and fall back to the fresh template build.
export function isPreCutoverDoc(state: unknown): boolean {
  if (!state || typeof state !== "object") return false;
  const root = state as { type?: string; content?: Array<{ type?: string }> };
  if (root.type !== "doc" || !Array.isArray(root.content)) return false;
  return !root.content.some(
    (child) => child?.type === "findingsSection" || child?.type === "impressionSection",
  );
}

// Drafts auto-saved during PM coercion bugs (multi-paragraph paste before the
// structure guard) sometimes carried orphan organSubsection nodes with empty
// kind/label. The guard blocks creating these now, but existing saved state
// may still contain them — treat as stale so the next load self-heals.
export function hasOrphanOrganSubsection(state: unknown): boolean {
  if (!state || typeof state !== "object") return false;
  if (Array.isArray(state)) return state.some(hasOrphanOrganSubsection);
  const n = state as {
    type?: string;
    attrs?: { kind?: unknown; label?: unknown };
    content?: unknown;
  };
  if (n.type === "organSubsection") {
    if (!n.attrs?.kind || !n.attrs?.label) return true;
  }
  return hasOrphanOrganSubsection(n.content);
}

// Pre-atom field shape: `{ type: "field", content: [{ type: "text", text: ... }] }`.
// The new schema is `atom: true` with text on `attrs.text`. Loading the old
// shape through PM silently drops the text (atoms don't accept content),
// rendering chips that are visually empty. Treat as stale so the next load
// rebuilds from the API template.
export function hasOldShapeFields(state: unknown): boolean {
  if (!state || typeof state !== "object") return false;
  if (Array.isArray(state)) return state.some(hasOldShapeFields);
  const n = state as { type?: string; content?: unknown; attrs?: { text?: unknown } };
  if (n.type === "field" && Array.isArray(n.content) && n.content.length > 0) {
    const hasTextAttr = typeof n.attrs?.text === "string" && (n.attrs.text as string).length > 0;
    if (!hasTextAttr) return true;
  }
  return hasOldShapeFields(n.content);
}

export function isStaleDraft(state: unknown): boolean {
  return (
    hasStaleMarks(state) ||
    isPreCutoverDoc(state) ||
    hasOrphanOrganSubsection(state) ||
    hasOldShapeFields(state)
  );
}

function draftSectionKinds(state: unknown): Set<string> | null {
  if (!state) return null;
  let root: JSONContent;
  try {
    root = typeof state === "string" ? (JSON.parse(state) as JSONContent) : (state as JSONContent);
  } catch {
    return null;
  }
  const findings = (root.content ?? []).find((node) => node.type === "findingsSection");
  if (!findings) return null;
  return new Set(
    (findings.content ?? [])
      .filter((node) => node.type === "organSubsection")
      .map((node) => node.attrs?.kind)
      .filter((kind): kind is string => typeof kind === "string" && kind.length > 0),
  );
}

/**
 * Detect a saved editor document created for a different report layout.
 *
 * New saves carry the exact backend-selected exam type, which is authoritative.
 * Older rows predate that field, so use a deliberately conservative structural
 * fallback: only a grossly different section set is considered a mismatch.
 * This catches the legacy abdomen skeleton around a head report without
 * treating a small template edit as permission to discard a radiologist's work.
 */
export function isDraftFromDifferentTemplate(
  state: unknown,
  savedExamType: string | null | undefined,
  template: ReportTemplate | null | undefined,
): boolean {
  if (!state || !template) return false;
  if (savedExamType) return savedExamType !== template.examType;

  const actual = draftSectionKinds(state);
  const expected = new Set(template.findings.map((section) => section.kind));
  if (!actual || actual.size < 3 || expected.size < 3) return false;
  const overlap = [...actual].filter((kind) => expected.has(kind)).length;
  const union = new Set([...actual, ...expected]).size;
  const symmetricDifference = union - overlap;
  return symmetricDifference >= 5 && overlap / union < 0.5;
}

/** Resolve the backend-owned template identity without inventing a frontend
 * default. Full AI report metadata wins, followed by the lightweight layout
 * response, then the identity persisted beside an existing draft. */
export function resolveReportExamType(
  reportExamType: string | null | undefined,
  layoutExamType: string | null | undefined,
  savedExamType: string | null | undefined,
): string | null {
  return reportExamType ?? layoutExamType ?? savedExamType ?? null;
}

/**
 * Splice a dictation transcript into an existing string at a caret offset.
 * Adds single-space padding at each boundary when neither side already
 * carries whitespace — so "word|word" + "more" produces "word more word",
 * not "wordmoreword". A null offset (Tab-focused chip with NodeSelection
 * and no inner caret, or strip card focused without a recorded caret)
 * falls back to end-of-text append with a separator space.
 *
 * Used by literal-mode dictation in both report chips and SuggestionStrip
 * cards — the surfaces differ but the splicing contract is identical.
 */
export function spliceAtCaret(
  existing: string,
  insert: string,
  caretOffset: number | null,
): string {
  if (caretOffset != null && caretOffset <= existing.length) {
    const before = existing.slice(0, caretOffset);
    const after = existing.slice(caretOffset);
    const padBefore = before && !/\s$/.test(before) && !/^\s/.test(insert) ? " " : "";
    const padAfter = after && !/\s$/.test(insert) && !/^\s/.test(after) ? " " : "";
    return before + padBefore + insert + padAfter + after;
  }
  const sep = existing && !/\s$/.test(existing) ? " " : "";
  return existing + sep + insert;
}

/**
 * Convert a dictation transcript into ProseMirror JSON. Each non-empty line
 * becomes one atomic `field` node carrying its text on `attrs.text`. Empty
 * lines become hardBreak separators.
 */
export function buildDictationContent(transcript: string): PMInsertContent {
  const lines = transcript.split("\n");
  const content: PMInsertContent = [];
  lines.forEach((line, i) => {
    if (i > 0) content.push({ type: "hardBreak" });
    if (line) {
      content.push({
        type: "field",
        attrs: { id: crypto.randomUUID(), kind: null, text: line },
      });
    }
  });
  return content;
}

/** Doc → final report text. Walks the structured tree and emits the
 * canonical "LABEL: text" lines for findings plus a numbered impression list.
 * paragraphToReportText handles the per-paragraph adjacency rules. */
export function getReportText(editor: { state: { doc: ProseMirrorNode } }): string {
  const findingsLines: string[] = [];
  const impressionLines: string[] = [];
  let hasGptImpression = false;

  editor.state.doc.descendants((node) => {
    if (node.type.name === "findingsSection") return true;
    if (node.type.name === "impressionSection") return true;

    if (node.type.name === "organSubsection") {
      const label = (node.attrs.label as string) || "";
      const lines: string[] = [];
      node.forEach((child) => {
        if (child.type.name === "paragraph") {
          const t = paragraphToReportText(child).trim();
          if (t) lines.push(t);
        }
      });
      if (lines.length > 0) {
        findingsLines.push(`${label}: ${lines.join(" ")}`);
      } else {
        findingsLines.push(`${label}:`);
      }
      return false;
    }

    if (node.type.name === "impressionItem") {
      const lines: string[] = [];
      node.forEach((child) => {
        if (child.type.name === "paragraph") {
          const t = paragraphToReportText(child).trim();
          if (t) lines.push(t);
        }
      });
      if (lines.length > 0) {
        if (node.attrs.provenance === "ai-detected") hasGptImpression = true;
        const idx = impressionLines.length + 1;
        impressionLines.push(`${idx}. ${lines.join(" ")}`);
      }
      return false;
    }

    return false;
  });

  const out: string[] = [];
  if (findingsLines.length > 0) {
    out.push("FINDINGS:", "", ...findingsLines);
  }
  if (impressionLines.length > 0) {
    if (out.length > 0) out.push("");
    out.push(
      "IMPRESSION:",
      "",
      ...(hasGptImpression ? [GPT_IMPRESSION_DISCLOSURE, ""] : []),
      ...impressionLines,
    );
  }
  return out.join("\n");
}

export type FieldConfidenceHtml = "confident" | "maybe";

const ATTR_LATERALITY_DISPLAY: Record<string, string> = {
  left: "Left",
  right: "Right",
  bilateral: "Bilateral",
  midline: "Midline",
};

const ATTR_ACUITY_DISPLAY: Record<string, string> = {
  acute: "acute",
  subacute: "subacute",
  chronic: "chronic",
  acute_on_chronic: "acute on chronic",
};

const ATTR_CALCIFICATION_DISPLAY: Record<string, string> = {
  calcified: "calcified",
  non_calcified: "non-calcified",
  partially_calcified: "partially calcified",
};

const ATTR_OBSTRUCTION_DISPLAY: Record<string, string> = {
  obstructing: "obstructing",
  nonobstructing: "non-obstructing",
};

/**
 * The measurement suffix appended after a finding phrase once the radiologist
 * measures the lesion on the viewer: " measuring A1 × A2 cm (V mL)" from the two
 * longest PCA axes and the volume. Shared by rebuildPhrase (legacy candidates
 * path) and joinPhrasingWithMeasurement (engine-phrasing path) so the rendered
 * measurement token is identical regardless of which produced the phrase head.
 */
export function measurementSuffix(axesCm: [number, number, number], volumeMl: number): string {
  const [a1, a2] = axesCm;
  return ` measuring ${a1.toFixed(1)} × ${a2.toFixed(1)} cm (${volumeMl.toFixed(1)} mL)`;
}

/**
 * Build a deterministic phrase from a base finding name + selected attributes.
 * Mirrors web-demo-backend/src/cortex_structured.py::build_default_phrase so
 * the frontend can rebuild the phrase locally when the radiologist toggles
 * attributes in the popover. Order: laterality → acuity → severity-or-size →
 * obstruction → calcification → base.
 */
export function rebuildPhrase(
  baseName: string,
  selected: Record<string, string | null>,
  candidates: ReadonlyArray<{ dim: string; value: string; probability: number }>,
  measurement?: { axesCm: [number, number, number]; volumeMl: number } | null,
): string {
  const parts: string[] = [];
  const lat = selected.laterality;
  if (lat) parts.push(ATTR_LATERALITY_DISPLAY[lat] ?? lat[0].toUpperCase() + lat.slice(1));

  const acu = selected.acuity;
  if (acu) parts.push(ATTR_ACUITY_DISPLAY[acu] ?? acu.replace(/_/g, " "));

  // When we have a real 3D measurement, drop the size descriptor — the
  // measurement supersedes "small/moderate/large".
  const sev = selected.severity;
  const size = measurement ? null : selected.size;
  if (sev && size) {
    const sevP = candidates.find((c) => c.dim === "severity" && c.value === sev)?.probability ?? 0;
    const sizeP = candidates.find((c) => c.dim === "size" && c.value === size)?.probability ?? 0;
    parts.push(sevP >= sizeP ? sev : size);
  } else if (sev) parts.push(sev);
  else if (size) parts.push(size);

  const obs = selected.obstruction;
  if (obs) parts.push(ATTR_OBSTRUCTION_DISPLAY[obs] ?? obs.replace(/_/g, " "));

  const calc = selected.calcification_status;
  if (calc) parts.push(ATTR_CALCIFICATION_DISPLAY[calc] ?? calc.replace(/_/g, " "));

  let phrase: string;
  if (parts.length > 0) {
    const head = parts[0][0].toUpperCase() + parts[0].slice(1);
    const rest = parts.slice(1).join(" ");
    const prefix = rest ? `${head} ${rest}` : head;
    phrase = prefix + " " + baseName.toLowerCase();
  } else {
    phrase = baseName;
  }
  if (measurement) {
    phrase += measurementSuffix(measurement.axesCm, measurement.volumeMl);
  }
  return phrase;
}

export function humanizeBaseFromCondition(condition: string): string {
  const last = condition.split("/").pop() ?? condition;
  const lower = last.replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Whether an AI finding has the ingredients required to rebuild its
 * phrase from a pill selection: a canonical condition slug AND at least
 * one candidate attribute. Surfaces that render the attribute pill UI
 * (InlineChipCard, SuggestionStrip) gate on this so toggling can't
 * silently produce a stack like "Moderate mild hepatic steatosis".
 *
 * An empty-string slug counts as absent: the condition attr defaults to
 * null and renderHTML omits falsy values, so "" only appears in
 * hand-corrupted drafts — rebuilding from it would emit a bare attribute
 * stack with no base phrase. Normalizing here keeps every caller (entry
 * gates, card render gate) agreeing without per-call-site coercion.
 */
export function canRebuildPhrase(
  candidates: ReadonlyArray<{ dim: string; value: string; probability: number }>,
  condition: string | null,
): boolean {
  return candidates.length > 0 && condition !== null && condition !== "";
}

/**
 * SSoT for "does this chip have an attribute-pill editing surface".
 * InlineChipCard's render gate and every entry point that offers "Edit
 * attributes" (chip right-click menu, E shortcut) MUST agree on this —
 * they previously didn't: the menu gated on candidates alone, so a chip
 * saved before the condition/phrasing attrs existed showed the item, and
 * the card then rendered nothing (issue #703's silent no-op).
 */
export function hasEditableAttributePills(
  phrasing: { dims: ReadonlyArray<unknown> } | null,
  candidates: ReadonlyArray<{ dim: string; value: string; probability: number }>,
  condition: string | null,
): boolean {
  if (phrasing != null && phrasing.dims.length > 0) return true;
  return canRebuildPhrase(candidates, condition);
}

/**
 * Parse the two phrase sources a measure-eligible chip carries off its node
 * attrs: engine-emitted `phrasing` (device-agnostic, preferred — the measurement
 * effect joins its pre-rendered tokens) and the legacy `candidates` array
 * (rebuildPhrase fallback for un-migrated cohorts). Each source degrades
 * independently: a corrupt or wrong-shape blob in one (logged) is treated as
 * absent, not fatal — the measure effect uses whichever is present and never
 * reads the other on that branch. Returns null only when NEITHER source is
 * usable (both corrupt, or a chip with no phrase data), so the caller bails out
 * of measure mode. Realistic corruption is hand-edited/legacy saved drafts;
 * engine-emitted chips always stringify real objects via aiFindingChipAttrs.
 */
export function parseChipPhraseSources(attrs: {
  phrasing?: unknown;
  candidates?: unknown;
}): { phrasing: Phrasing | null; candidates: CandidateAttribute[] } | null {
  let phrasing: Phrasing | null = null;
  if (typeof attrs.phrasing === "string") {
    try {
      const parsed = JSON.parse(attrs.phrasing) as Phrasing;
      // Shape-check: valid JSON of the wrong shape (e.g. `123`, `{"a":1}`) would
      // slip past a null check and then crash phrasingDefaultSelection's `for…of`.
      if (parsed && Array.isArray(parsed.dims)) {
        phrasing = parsed;
      } else {
        logger.warn("measure_phrasing_shape_invalid");
      }
    } catch (err) {
      logger.warn("measure_phrasing_parse_failed err=%o", err);
    }
  }
  let candidates: CandidateAttribute[] = [];
  if (typeof attrs.candidates === "string") {
    try {
      const parsed = JSON.parse(attrs.candidates);
      // Same shape-check: a non-array (e.g. `{}`) passes `.length === 0` as
      // `undefined === 0` (false) and crashes defaultAttributeSelection's `for…of`.
      if (Array.isArray(parsed)) {
        candidates = parsed;
      } else {
        logger.warn("measure_candidate_shape_invalid");
      }
    } catch (err) {
      // A corrupt `candidates` blob must NOT suppress measure mode when `phrasing`
      // is valid — the measure effect renders via joinPhrasingWithMeasurement and
      // never reads candidates on that branch. Treat as absent and let the guard
      // below bail only when NEITHER source is usable.
      logger.warn("measure_candidate_parse_failed err=%o", err);
    }
  }
  if (!phrasing && candidates.length === 0) return null;
  return { phrasing, candidates };
}

/**
 * Canonical condition slug for a chip. The node's `condition` attr is the
 * SSoT (stamped since the condition/phrasing attr rollout); chips from
 * drafts saved before it existed fall back to an exact text match against
 * the AI draft — which only holds while the chip text is unedited, so the
 * fallback logs. Returning null means measure/rebuild flows can't run;
 * callers surface that rather than silently no-oping (issue #703 class).
 */
export function resolveChipCondition(
  attrs: Record<string, unknown>,
  sections: Record<string, ReportSection> | undefined,
): string | null {
  const attrCondition = attrs.condition;
  if (typeof attrCondition === "string" && attrCondition !== "") return attrCondition;
  const currentText = (attrs.text as string | null) ?? "";
  if (currentText === "") return null;
  for (const sec of Object.values(sections ?? {})) {
    const f = sec.findings.find((mf) => mf.text === currentText);
    if (f?.condition) {
      logger.warn("chip_condition_from_text_match");
      return f.condition;
    }
  }
  return null;
}

/**
 * Stable warn ids for chip entry-point refusals. Each id names the branch
 * that fired (silent-execution-is-a-bug norm); the component maps them to
 * user-facing toast copy via an exhaustive Record, so a new refusal reason
 * can't ship without copy.
 */
export type ChipGateWarnId =
  | "chip_edit_attrs_unavailable"
  | "measure_chip_not_found"
  | "measure_no_phrase_sources"
  | "measure_no_condition";

/**
 * Gate for the "Edit attributes" entry points (chip right-click menu item,
 * E shortcut). This is the SAME function the menu-display predicate uses,
 * so what the menu offers is exactly what the action can do — the two
 * drifting apart is how issue #703's silent no-op happened. Resolves the
 * chip by id (never posAtDOM — the field is an atom, so DOM positions land
 * in the parent paragraph) and requires the pill surface the card renders.
 */
export function gateEditAttrsForChip(
  doc: ProseMirrorNode,
  chipId: string,
): { ok: true } | { ok: false; warnId: "chip_edit_attrs_unavailable" } {
  const refusal = { ok: false, warnId: "chip_edit_attrs_unavailable" } as const;
  const hit = findFieldNodeById(doc, chipId);
  if (!hit) return refusal;
  const sources = parseChipPhraseSources(hit.node.attrs);
  if (!sources) return refusal;
  return hasEditableAttributePills(
    sources.phrasing,
    sources.candidates,
    (hit.node.attrs.condition as string | null) ?? null,
  )
    ? { ok: true }
    : refusal;
}

export type MeasureChipGate =
  | {
      ok: true;
      fieldStart: number;
      fieldEnd: number;
      condition: string;
      phrasing: Phrasing | null;
      candidates: CandidateAttribute[];
    }
  | {
      ok: false;
      warnId: "measure_chip_not_found" | "measure_no_phrase_sources" | "measure_no_condition";
    };

/**
 * Gate for the "Add measurement" entry points (chip menu item, M shortcut),
 * shared with the menu-display predicate (see gateEditAttrsForChip on why).
 * On success it returns everything it proved along the way — the chip's
 * range, resolved condition, and parsed phrase sources — so the caller
 * builds the measure request from the gated values instead of re-deriving
 * them. Refusals are per-branch: distinct warn ids for a vanished chip,
 * unusable phrase sources, and an unresolvable condition.
 */
export function gateMeasureForChip(
  doc: ProseMirrorNode,
  chipId: string,
  sections: Record<string, ReportSection> | undefined,
): MeasureChipGate {
  const hit = findFieldNodeById(doc, chipId);
  if (!hit) return { ok: false, warnId: "measure_chip_not_found" };
  const sources = parseChipPhraseSources(hit.node.attrs);
  if (!sources) return { ok: false, warnId: "measure_no_phrase_sources" };
  const condition = resolveChipCondition(hit.node.attrs, sections);
  if (!condition) return { ok: false, warnId: "measure_no_condition" };
  return {
    ok: true,
    fieldStart: hit.pos,
    fieldEnd: hit.pos + hit.node.nodeSize,
    condition,
    phrasing: sources.phrasing,
    candidates: sources.candidates,
  };
}

/**
 * Toggle a single dim → value pair in a candidate-attribute selection.
 * Setting `value` again on the same dim clears it (null). Pure — returns
 * a new map, doesn't mutate the input.
 *
 * Used by both InlineChipCard and SuggestionStrip pill toggles so the
 * "click same pill again to deselect" semantics stay consistent across
 * both AI surfaces.
 */
export function toggleAttrSelection(
  current: Record<string, string | null>,
  dim: string,
  value: string,
): Record<string, string | null> {
  const next = { ...current };
  next[dim] = next[dim] === value ? null : value;
  return next;
}

/**
 * Rebuild an AI-finding phrase from its canonical condition slug and the
 * radiologist's current attribute selection. The slug (e.g.
 * "liver/hepatic_steatosis") is humanized into a base name ("Hepatic
 * steatosis"), then rebuildPhrase prepends laterality / severity.
 *
 * Passing an already-rendered phrase ("Mild hepatic steatosis") as the
 * base would stack adjectives — toggling "moderate" would emit "Moderate
 * mild hepatic steatosis". Always feed the condition SLUG, not the
 * rendered text. Strip cards and chips share this call site so any future
 * fix to the rebuild contract lands in one place.
 */
export function rebuildPhraseFromCondition(
  condition: string,
  selected: Record<string, string | null>,
  candidates: ReadonlyArray<{ dim: string; value: string; probability: number }>,
): string {
  return rebuildPhrase(humanizeBaseFromCondition(condition), selected, candidates);
}

/** Pick the default selection: for each dim, the highest-prob candidate above
 * 0.5, else null. Mirrors backend default_phrase logic. */
export function defaultAttributeSelection(
  candidates: ReadonlyArray<{ dim: string; value: string; probability: number }>,
): Record<string, string | null> {
  const byDim: Record<string, { value: string; prob: number } | null> = {};
  for (const c of candidates) {
    if (c.probability < 0.5) continue;
    const cur = byDim[c.dim];
    if (!cur || c.probability > cur.prob) {
      byDim[c.dim] = { value: c.value, prob: c.probability };
    }
  }
  const out: Record<string, string | null> = {};
  for (const dim of Object.keys(byDim)) out[dim] = byDim[dim]?.value ?? null;
  return out;
}

// ── Engine-emitted phrasing (the device-agnostic path) ─────────────────────────
// When a finding carries `phrasing`, the engine already rendered every value to its
// display token through the shared spec. The client does NOT re-derive clinical text
// — it only joins selected tokens. These two helpers are the exact mirror of the
// engine's phrase_from_phrasing + mutex resolution, so the chip's live re-render on
// pill toggle stays identical to what the engine emitted (no drift, no spec here).

/** Default pill selection from the engine's gated picks. */
export function phrasingDefaultSelection(phrasing: Phrasing): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const d of phrasing.dims) out[d.dim] = d.selected;
  return out;
}

/** Join the selected option tokens into the flat phrase. Mirror of the engine's
 *  phrase_from_phrasing: tokens in dim order, sentence-case the leading one, append
 *  the lowercased base. `selected` overrides the engine picks (live toggle state). */
export function joinPhrasing(phrasing: Phrasing, selected?: Record<string, string | null>): string {
  const parts: string[] = [];
  for (const d of phrasing.dims) {
    const sel = selected ? selected[d.dim] : d.selected;
    if (!sel) continue;
    const opt = d.options.find((o) => o.value === sel);
    if (opt?.text) parts.push(opt.text);
  }
  const base = phrasing.base;
  if (parts.length === 0) return base.charAt(0).toUpperCase() + base.slice(1);
  const head = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  return [head, ...parts.slice(1), base.toLowerCase()].join(" ");
}

/**
 * Engine-phrasing analog of rebuildPhrase's measurement branch. Joins the
 * selected option tokens (joinPhrasing — no client-side clinical logic) and
 * appends the measurement suffix. Engine `phrasing` has no measurement slot
 * (measurements are user-entered, not engine-emitted), so the suffix is injected
 * here on top of the spec-rendered phrase.
 *
 * Like rebuildPhrase, a real 3D measurement supersedes the size descriptor: the
 * "size" dim is cleared before joining, so the phrase reads "Calcified nodule
 * measuring 1.2 × 0.9 cm" rather than "Small calcified nodule measuring …".
 */
export function joinPhrasingWithMeasurement(
  phrasing: Phrasing,
  selected: Record<string, string | null>,
  measurement: { axesCm: [number, number, number]; volumeMl: number },
): string {
  const withoutSize = { ...selected, size: null };
  return (
    joinPhrasing(phrasing, withoutSize) +
    measurementSuffix(measurement.axesCm, measurement.volumeMl)
  );
}

/** Toggle a value for a dim (click the selected pill again to clear), honoring the
 *  engine's mutex groups: selecting a value clears any selection in the same group
 *  (e.g. picking a size clears severity). Mirror of build_phrasing's mutex rule. */
export function togglePhrasingSelection(
  phrasing: Phrasing,
  current: Record<string, string | null>,
  dim: string,
  value: string,
): Record<string, string | null> {
  const next = { ...current };
  next[dim] = next[dim] === value ? null : value;
  if (next[dim] !== null) {
    const group = phrasing.dims.find((d) => d.dim === dim)?.mutex_group;
    if (group) {
      for (const d of phrasing.dims) {
        if (d.dim !== dim && d.mutex_group === group) next[d.dim] = null;
      }
    }
  }
  return next;
}

/** The subset of an AI finding (MergeField / ToConsiderItem / StripItem)
 *  needed to mint a report chip. All three wire shapes carry these. The
 *  candidate shape is the structural minimum the rest of this module uses
 *  (`rebuildPhrase`, `defaultAttributeSelection`) — extra fields like
 *  `opLevel` ride through `JSON.stringify` when present. */
export type AiFindingLike = Pick<
  MergeField,
  | "id"
  | "text"
  | "condition"
  | "seriesId"
  | "sliceIndex"
  | "frameOfReferenceUid"
  | "sourceRef"
  | "sourcePointer"
  | "sourceSnapshot"
  | "sourceStatus"
  | "attributeSources"
  | "imageRefs"
  | "wording"
> & {
  candidateAttributes?: ReadonlyArray<{ dim: string; value: string; probability: number }>;
  attributes?: ReadonlyArray<{ dim: string; value: string }>;
  phrasing?: Phrasing | null;
};

/**
 * Canonical JSON encoding for a chip's `candidates` attr: sorted by
 * descending probability, then `JSON.stringify`. Both the inline build
 * (buildReportDoc) and strip accept route through this so the stored
 * ordering can't drift between the two surfaces.
 */
export function encodeCandidates(
  candidates: ReadonlyArray<{ dim: string; value: string; probability: number }> | undefined,
): string | null {
  if (!candidates || candidates.length === 0) return null;
  const sorted = [...candidates].sort((a, b) => b.probability - a.probability);
  return JSON.stringify(sorted);
}

/** Preserve the regulated Drafter attribute list exactly as emitted. */
export function encodeSelectedAttributes(
  attributes: ReadonlyArray<{ dim: string; value: string }> | undefined,
): string | null {
  return attributes ? JSON.stringify(attributes) : null;
}

/**
 * Single source of truth for the attrs an AI-finding chip carries. Used by
 * BOTH injection paths — buildReportDoc (inline, build-time) and
 * SuggestionStrip → acceptIntoSection (strip, runtime) — so the two can't
 * diverge on which attrs a chip gets (the bug that dropped `condition` and
 * left candidate ordering unsorted on the strip path). Adding a new chip
 * attr means editing only this function.
 */
export function aiFindingChipAttrs(
  f: AiFindingLike,
  confidence: FieldConfidenceHtml,
): Partial<FieldNodeAttrs> {
  return {
    id: f.id,
    source: JSON.stringify({
      sourceRef: f.sourceRef ?? null,
      sourcePointer: f.sourcePointer ?? null,
      sourceSnapshot: f.sourceSnapshot ?? null,
      sourceStatus: f.sourceStatus ?? "unavailable",
      imageRefs: f.imageRefs ?? [],
      ...(f.attributeSources ? { attributeSources: f.attributeSources } : {}),
      ...(f.wording ? { wording: f.wording } : {}),
    }),
    text: f.text,
    kind: "ai-finding",
    confidence,
    seriesId: f.seriesId ?? null,
    sliceIndex: f.sliceIndex ?? null,
    frameOfReferenceUid: f.frameOfReferenceUid ?? null,
    candidates: encodeCandidates(f.candidateAttributes),
    selectedAttributes: encodeSelectedAttributes(f.attributes),
    phrasing: f.phrasing ? JSON.stringify(f.phrasing) : null,
    condition: f.condition ?? null,
  };
}

/** A field node as ProseMirror JSON. FieldNode is `atom: true`; the text
 *  lives on `attrs.text`, so the node carries no content. */
function fieldNode(attrs: Partial<FieldNodeAttrs>): JSONContent {
  return { type: "field", attrs };
}

/** One paragraph holding the given chips, separated by a single space text
 *  node so adjacent chips don't render flush (mirrors acceptIntoSection's
 *  append-with-space and the old HTML `join(" ")`). */
function paragraphNode(chips: JSONContent[]): JSONContent {
  const content: JSONContent[] = [];
  chips.forEach((chip, i) => {
    if (i > 0) content.push({ type: "text", text: " " });
    content.push(chip);
  });
  return { type: "paragraph", content };
}

// Default-text chip ids are deterministic (section + suffix) so re-renders
// of the same report don't churn the chip's identity. The backend's
// MergeField.id supplies real identifiers for AI-detected findings.
function defaultChipId(section: string): string {
  return `default-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

export interface ToConsiderInput {
  id: string;
  text: string;
  condition: string;
  section: string;
  seriesId: string | null;
  sliceIndex: number | null;
  frameOfReferenceUid: string | null;
  candidateAttributes?: ReadonlyArray<{ dim: string; value: string; probability: number }>;
  attributes?: ReadonlyArray<{ dim: string; value: string }>;
  // Engine-emitted phrasing, read by aiFindingChipAttrs. Declared so the buildReportDoc
  // contract matches what's encoded onto the chip (the "maybe" chips otherwise carried it
  // only by structural luck from the raw ToConsiderItem).
  phrasing?: Phrasing | null;
}

/**
 * Build the editor's initial document (ProseMirror JSON) from a
 * backend-served template plus the AI findings response. Each organ
 * subsection becomes an `organSubsection` node (label rendered by its
 * NodeView, never editable); AI-detected findings appear as `field` chips,
 * mixing "confident" (from sections) and "maybe" (from toConsider) per their
 * section. Sections the AI didn't flag get the negative-template default.
 * Impression content is always generated separately from these exact fields.
 *
 * Emitting PM JSON (not an HTML string) means `FieldNode.renderHTML` is the
 * one and only chip serializer; chip attrs come from `aiFindingChipAttrs`,
 * shared with the strip accept path. No hand-rolled HTML escaping, no second
 * place to drift.
 *
 * This is the SOLE injection path for AI content. There is no separate
 * reconciler walking the doc on every update — the saved editor state IS
 * the source of truth once loaded. Reconciliation between the AI state
 * and the doc happens ONCE here, then PM's transactional model takes over.
 */
export function buildReportDoc(
  template: Pick<ReportTemplate, "findings">,
  sections: Record<string, { findings: MergeField[] }>,
  accepted: Record<string, MergeField[]> = {},
  toConsider: ReadonlyArray<ToConsiderInput> = [],
): JSONContent {
  const toConsiderBySection = new Map<string, ToConsiderInput[]>();
  for (const item of toConsider) {
    const arr = toConsiderBySection.get(item.section);
    if (arr) arr.push(item);
    else toConsiderBySection.set(item.section, [item]);
  }

  const subsections: JSONContent[] = template.findings.map((sub) => {
    const confident = sections[sub.kind]?.findings || [];
    const acceptedForSub = accepted[sub.kind] || [];
    const maybe = toConsiderBySection.get(sub.kind) || [];

    const confidentChips = [...confident, ...acceptedForSub].map((f) =>
      fieldNode(aiFindingChipAttrs(f, "confident")),
    );
    const maybeChips = maybe.map((m) => fieldNode(aiFindingChipAttrs(m, "maybe")));
    const aiChips = [...confidentChips, ...maybeChips];

    const chips =
      aiChips.length === 0
        ? [fieldNode({ id: defaultChipId(sub.kind), text: sub.default, kind: "template" })]
        : aiChips;

    return {
      type: "organSubsection",
      attrs: { kind: sub.kind, label: sub.label },
      content: [paragraphNode(chips)],
    };
  });

  const content: JSONContent[] = [];
  // findingsSection requires `organSubsection+` — omit it entirely for an
  // empty template rather than emit a schema-invalid empty section.
  if (subsections.length > 0) {
    content.push({ type: "findingsSection", content: subsections });
  }
  content.push({ type: "impressionSection" });

  return { type: "doc", content };
}

/**
 * Normalized signature of a doc's FINDINGS portion only: for each
 * `organSubsection` (in document order) its `kind` plus each `field` chip's
 * identifying attrs `{kind, id, text, condition}`. The impression is
 * deliberately excluded — the auto-generated "normal" impression the poison
 * path leaves behind is not rad work. Only these four attrs are read, so a
 * draft normalized through the editor (every attr filled with its schema
 * default) compares equal to a raw `buildReportDoc` output. Returns null when
 * there is no findingsSection (not a report doc).
 */
function findingsSignature(doc: JSONContent | null | undefined): string | null {
  if (!doc || typeof doc !== "object") return null;
  const findings = (doc.content ?? []).find((n) => n.type === "findingsSection");
  if (!findings) return null;
  const subs = (findings.content ?? []).map((sub) => ({
    kind: sub.attrs?.kind ?? null,
    chips: (sub.content ?? [])
      .flatMap((block) => block.content ?? [])
      .filter((n) => n.type === "field")
      .map((f) => ({
        kind: f.attrs?.kind ?? null,
        id: f.attrs?.id ?? null,
        text: f.attrs?.text ?? null,
        condition: f.attrs?.condition ?? null,
      })),
  }));
  return JSON.stringify(subs);
}

/**
 * True when `state` is an unedited negative-template draft — its findings
 * portion is identical (by {@link findingsSignature}) to a fresh
 * `buildReportDoc(template)` with no findings folded in.
 *
 * This detects a phantom "empty" saved draft that was auto-created before a
 * study's AI findings were available (opened pre-inference), so the reporter
 * can safely rebuild from the now-available findings without discarding any rad
 * work. Accepts a ProseMirror-JSON object or its JSON string.
 *
 * Strict by design: any folded finding, rad-added chip, or edited default line
 * makes it false. That fail-safe bias means we only ever auto-rebuild over a
 * provably untouched template — never over real work (a non-match just leaves
 * the draft as-is, same as today, with manual "Reset draft" still available).
 */
export function isUnworkedTemplateDraft(state: unknown, template: ReportTemplate): boolean {
  if (!state) return false;
  let doc: JSONContent | null;
  try {
    doc = typeof state === "string" ? (JSON.parse(state) as JSONContent) : (state as JSONContent);
  } catch {
    return false;
  }
  const actual = findingsSignature(doc);
  if (actual === null) return false;
  return actual === findingsSignature(buildReportDoc(template, {}, {}, []));
}

export interface ImpressionItemInput {
  id: string;
  text: string;
  citedFindings: string[];
}

function impressionItemNode(item: ImpressionItemInput): JSONContent {
  return {
    type: "impressionItem",
    attrs: { id: item.id, citedFindings: item.citedFindings, provenance: "ai-detected" },
    content: [
      paragraphNode([
        fieldNode({
          id: item.id,
          text: item.text,
          kind: "ai-finding",
        }),
      ]),
    ],
  };
}

/**
 * Build the impressionSection node (PM JSON) for the streaming-impression
 * renderer. Each item becomes impressionItem → paragraph → ai-finding chip.
 * While still streaming with no items yet, a single zero-width placeholder
 * item keeps the section non-empty so the layout doesn't jump.
 *
 * Impression chips are minimal (id + text) — they carry no cortex metadata,
 * so they're built directly via fieldNode rather than aiFindingChipAttrs.
 * Sharing fieldNode/paragraphNode with buildReportDoc means impression chips
 * serialize through FieldNode.renderHTML like every other chip — no
 * hand-rolled HTML string on the streaming path.
 */
export function buildImpressionSection(
  items: ReadonlyArray<ImpressionItemInput>,
  loading: boolean,
): JSONContent {
  const itemNodes = items.map((item) => impressionItemNode(item));

  if (itemNodes.length === 0 && loading) {
    // Zero-width-space placeholder while the first item is still in flight.
    itemNodes.push({
      type: "impressionItem",
      attrs: { provenance: "ai-detected" },
      content: [{ type: "paragraph", content: [{ type: "text", text: "\u200B" }] }],
    });
  }

  return { type: "impressionSection", content: itemNodes };
}
